/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from 'react';
import { Input, notification, Select, AutoComplete, Radio } from 'antd';
import { Button as CrceButton } from '@crc-evans/ui-components';
import { X } from 'lucide-react';
import './ShipmentDashBoardPage.css';
import { OnFetchShipmentSno } from '../../redux/ShipmentDetails/saga';
import { OnAddorUpdateShipmentsDashboard } from '../../redux/ShipmentDashboardDetails/saga';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
  packagingFormValueToApi,
} from './shipmentFieldOptions';

const { Option } = Select;

const AddNewShipments = (props) => {
  const {
    showAddModel,
    setShowAddModel,
    editRecord,
    isEdit,
    setIsEdit,
    setEditRecord,
    OnfetchData,
    selectedProject,
    adminShipmentTypesList,
  } = props;

  const [shipmentType, setShipmentType] = useState('');
  const [ShipmentNumber, setShipmentNumber] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const [serialNoList, setSerialNoList] = useState([]);
  const [serialNo, setSerialNo] = useState('');
  const [shipmentTyCategory, setShipmentTypeCategory] = useState('');
  const [serialNoLoading, setSerialNoLoading] = useState(false);

  const [priority, setPriority] = useState('medium');
  const [packagingSubcategory, setPackagingSubcategory] = useState(undefined);
  const [shipmentCategory, setShipmentCategory] = useState('project');

  const OnfetchSerialNumberData = useCallback(async (selectedShipmentType) => {
    setSerialNoLoading(true);
    const obj = { id: selectedShipmentType.id };
    setShipmentType(selectedShipmentType.name);
    const res = await OnFetchShipmentSno(obj, 'false');
    const list = res?.shipmentType?.shipments && res.shipmentType.shipments.length > 0
      ? res.shipmentType.shipments
      : [];
    setSerialNoList(list);
    setSerialNoLoading(false);
    return list;
  }, []);

  useEffect(() => {
    if (!isEdit || !editRecord || !adminShipmentTypesList?.length) return;

    const run = async () => {
      const shipmentArr = adminShipmentTypesList.filter(
        (item) => item.name === editRecord.shipmentTypeName
      );
      if (!shipmentArr.length) return;

      setShipmentTypeCategory(shipmentArr[0]);
      setShipmentType(shipmentArr[0].name);

      const meta = normalizeShipmentMetaFromRecord(editRecord);
      setPriority(meta.priority);
      setPackagingSubcategory(meta.packagingSubcategory);
      setShipmentCategory(meta.shipmentCategory);

      const list = await OnfetchSerialNumberData(shipmentArr[0]);
      const num = editRecord.shipmentNumber;
      setShipmentNumber(num || '');
      const match = list.find((item) => item.shipmentNumber === num);
      setSerialNo(match || (num ? { shipmentNumber: num } : ''));
    };

    run();
  }, [isEdit, editRecord, adminShipmentTypesList, OnfetchSerialNumberData]);

  const onChangeShipmentType = async (value) => {
    setSerialNo('');
    setShipmentNumber('');
    if (value) {
      const shipmentArr = adminShipmentTypesList?.filter((item) => item.name === value) || [];
      if (shipmentArr.length > 0) {
        setShipmentTypeCategory(shipmentArr[0]);
        await OnfetchSerialNumberData(shipmentArr[0]);
      } else {
        setShipmentTypeCategory('');
        setShipmentType('');
        setSerialNoList([]);
      }
    } else {
      setShipmentTypeCategory('');
      setShipmentType('');
      setSerialNoList([]);
      setShipmentNumber('');
    }
  };

  const onShipmentNumberChange = (value) => {
    const v = typeof value === 'string' ? value.trim() : '';
    setShipmentNumber(v);
    if (!v) {
      setSerialNo('');
      return;
    }
    const match = serialNoList.find((item) => item.shipmentNumber === v);
    setSerialNo(match || { shipmentNumber: v });
  };

  const autoCompleteOptions = serialNoList.map((item) => ({
    value: item.shipmentNumber,
    label: item.shipmentNumber,
  }));

  const prioritySelectValue = SHIPMENT_PRIORITY_OPTIONS.some((o) => o.value === priority)
    ? priority
    : undefined;
  const packagingSelectValue =
    packagingSubcategory &&
    SHIPMENT_PACKAGING_OPTIONS.some((o) => o.value === packagingSubcategory)
      ? packagingSubcategory
      : undefined;
  const shipmentTypeNames = (adminShipmentTypesList || []).map((item) => item?.name).filter(Boolean);
  const shipmentTypeSelectValue =
    shipmentType && shipmentTypeNames.includes(shipmentType) ? shipmentType : undefined;

  const handleOk = async () => {
    if (shipmentTyCategory === '') {
      notification.warning({
        message: 'Please select shipment type to proceed!',
      });
      return;
    }
    const num = (ShipmentNumber || '').trim();
    if (!num) {
      notification.warning({
        message: 'Please enter or select a shipment number to proceed!',
      });
      return;
    }
    if (!packagingSubcategory) {
      notification.warning({
        message: 'Please select packaging (subcategory) to proceed!',
      });
      return;
    }
    const resolvedPriority = SHIPMENT_PRIORITY_OPTIONS.some((o) => o.value === priority)
      ? priority
      : 'medium';

    const data = {
      shipmentTypeId: shipmentTyCategory.id,
      projectId: selectedProject.id,
      status: 1,
      priority: resolvedPriority,
      shipmentCategory: shipmentCategory,
      packagingType: packagingFormValueToApi(packagingSubcategory),
    };

    const matched = serialNoList.find((item) => item.shipmentNumber === num);
    if (matched?.id) {
      data.shipmentId = matched.id;
    } else if (serialNo?.id) {
      data.shipmentId = serialNo.id;
    } else {
      data.shipmentNumber = num;
    }

    let payload = data;
    if (isEdit) {
      payload = {
        id: editRecord.pihShipmentId,
        data,
      };
    }

    setAddLoading(true);
    const res = await OnAddorUpdateShipmentsDashboard(payload, isEdit);

    if (res) await OnfetchData();
    else {
      notification.warning({
        message: `${shipmentType} and ${ShipmentNumber} could not be saved. It may already exist.`,
      });
    }

    setAddLoading(false);
    resetForm();
    setShowAddModel(false);
  };

  const resetForm = () => {
    setShipmentType('');
    setShipmentNumber('');
    setSerialNo('');
    setSerialNoList([]);
    setShipmentTypeCategory('');
    setPriority('medium');
    setShipmentCategory('project');
    setPackagingSubcategory(undefined);
    setIsEdit(false);
    if (setEditRecord) setEditRecord('');
  };

  const handleCancel = () => {
    setShowAddModel(false);
    resetForm();
  };

  if (!showAddModel) return null;

  return (
    <div className="mc-dpr-modal-backdrop" role="presentation">
      <div
        className="mc-dpr-modal mc-dpr-modal--shipment-form"
        role="dialog"
        aria-labelledby="ship-add-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mc-dpr-modal-head">
          <h3 id="ship-add-modal-title" className="mc-dpr-modal-title">
            {isEdit ? 'Edit shipment' : 'Add shipment'}
          </h3>
          <button type="button" className="mc-dpr-modal-close" aria-label="Close" onClick={handleCancel}>
            <X size={20} />
          </button>
        </div>
        <div className="mc-dpr-modal-body">
          <div className="mc-dpr-modal-form-grid mc-dpr-modal-form-grid--shipment">
            <div className="mc-dpr-modal-form-row">
              <div className="mc-dpr-modal-field">
                <label htmlFor="ship-add-priority">
                  Priority <span className="required">*</span>
                </label>
                <Select
                  id="ship-add-priority"
                  className="mc-dpr-modal-field-control material-select"
                  style={{ width: '100%' }}
                  placeholder="Select priority level"
                  allowClear
                  value={prioritySelectValue}
                  onChange={setPriority}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                >
                  {SHIPMENT_PRIORITY_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="mc-dpr-modal-form-row">
              <div className="mc-dpr-modal-field">
                <span className="mc-dpr-modal-field-label">
                  Category <span className="required">*</span>
                </span>
                <Radio.Group
                  className="shipment-category-radio-group"
                  value={shipmentCategory}
                  onChange={(e) => setShipmentCategory(e.target.value)}
                >
                  {SHIPMENT_CATEGORY_OPTIONS.map((o) => (
                    <Radio key={o.value} value={o.value}>{o.label}</Radio>
                  ))}
                </Radio.Group>
              </div>
            </div>
            <div className="mc-dpr-modal-form-row">
              <div className="mc-dpr-modal-field">
                <label htmlFor="ship-add-type">
                  Shipment type <span className="required">*</span>
                </label>
                <Select
                  id="ship-add-type"
                  className="mc-dpr-modal-field-control material-select"
                  style={{ width: '100%' }}
                  placeholder="Select or search shipment type"
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  onChange={onChangeShipmentType}
                  value={shipmentTypeSelectValue}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                >
                  {adminShipmentTypesList &&
                    adminShipmentTypesList.map((item, i) => (
                      <Option key={i} value={item && item.name}>
                        {item && item.name}
                      </Option>
                    ))}
                </Select>
              </div>
            </div>
            <div className="mc-dpr-modal-form-row">
              <div className="mc-dpr-modal-field">
                <label htmlFor="ship-add-number">
                  Shipment number <span className="required">*</span>
                </label>
                <AutoComplete
                  id="ship-add-number"
                  className="mc-dpr-modal-field-control material-select"
                  style={{ width: '100%' }}
                  options={autoCompleteOptions}
                  placeholder={
                    serialNoLoading
                      ? 'Loading shipment numbers…'
                      : 'Select or type shipment number'
                  }
                  disabled={!shipmentType}
                  value={ShipmentNumber || undefined}
                  onChange={onShipmentNumberChange}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                  notFoundContent={
                    serialNoLoading
                      ? 'Loading…'
                      : shipmentType
                        ? 'No saved numbers for this type — type a new number.'
                        : 'Select a shipment type first.'
                  }
                  filterOption={(inputValue, option) => {
                    const opt =
                      option?.value ??
                      option?.label ??
                      (typeof option === 'string' ? option : '');
                    return String(opt)
                      .toLowerCase()
                      .includes(String(inputValue || '').toLowerCase());
                  }}
                >
                  <Input autoComplete="off" />
                </AutoComplete>
                <p className="shipment-modal-hint" style={{ marginTop: '6px' }}>
                  Choose from suggestions or type a shipment number.
                </p>
              </div>
            </div>
            <div className="mc-dpr-modal-form-row mc-dpr-modal-form-row--span-2">
              <div className="mc-dpr-modal-field">
                <label htmlFor="ship-add-packaging">
                  Subcategory (packaging) <span className="required">*</span>
                </label>
                <Select
                  id="ship-add-packaging"
                  className="mc-dpr-modal-field-control material-select"
                  style={{ width: '100%' }}
                  placeholder="Select packaging (subcategory)"
                  allowClear
                  value={packagingSelectValue}
                  onChange={setPackagingSubcategory}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  dropdownClassName="heat-profile-select-dropdown mc-dpr-modal-select-dd"
                >
                  {SHIPMENT_PACKAGING_OPTIONS.map((o) => (
                    <Option key={o.value} value={o.value}>{o.label}</Option>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        </div>
        <div className="mc-dpr-modal-foot">
          <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={handleCancel} />
          <CrceButton
            label={addLoading ? (isEdit ? 'Updating…' : 'Adding…') : isEdit ? 'Update' : 'Add'}
            variant="primary"
            size="medium"
            onClick={handleOk}
            disabled={addLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default AddNewShipments;
